import React from "react";

import { GuiState, useGuiState } from "@framework/GuiMessageBroker";
import { loadWorkbenchSessionFromLocalStorage } from "@framework/internal/WorkbenchSession/utils/loaders";
import {
    extractDashboardPreviewItems,
    type WorkbenchSessionDataContainer,
} from "@framework/internal/WorkbenchSession/utils/WorkbenchSessionDataContainer";
import type { Workbench } from "@framework/Workbench";
import { AlertDialog } from "@lib/components/AlertDialog";
import { TimeAgo } from "@lib/components/TimeAgo";

import { useActiveSession } from "../ActiveSessionBoundary";
import { DashboardPreviewCarousel } from "../DashboardPreview/dashboardPreviewCarousel";

export type ActiveSessionRecoveryDialogProps = {
    workbench: Workbench;
};

export function ActiveSessionRecoveryDialog(props: ActiveSessionRecoveryDialogProps): React.ReactNode {
    const [isOpen, setIsOpen] = useGuiState(
        props.workbench.getGuiMessageBroker(),
        GuiState.ActiveSessionRecoveryDialogOpen,
    );

    const activeSession = useActiveSession();

    const [sessionData, setSessionData] = React.useState<WorkbenchSessionDataContainer | null>(null);

    const loadSession = React.useCallback(
        async function loadSession() {
            try {
                const storedSession = loadWorkbenchSessionFromLocalStorage(activeSession.getId());
                setSessionData(storedSession);
            } catch {
                setSessionData(null);
            }
        },
        [activeSession],
    );

    React.useEffect(
        function loadSessionOnOpen() {
            if (isOpen) {
                loadSession();
            }
        },
        [isOpen, loadSession],
    );

    if (!isOpen || !sessionData) {
        return null;
    }

    function handleDiscard() {
        props.workbench.getSessionManager().discardLocalStorageSession(activeSession.getId(), false);
        setIsOpen(false);
    }

    function handleOpen() {
        props.workbench.getSessionManager().updateFromLocalStorage();
    }

    return (
        <AlertDialog
            open={isOpen}
            onOpenChange={setIsOpen}
            title="Do you want to recover your previous session?"
            primaryAction={{ label: "Recover session", onClick: handleOpen }}
            secondaryActions={[{ label: "Delete session", onClick: handleDiscard, tone: "danger" }]}
        >
            <div className="gap-y-sm flex flex-col">
                We found an unsaved version of your current session in your browser. You can either delete or recover
                it.
                <div className="gap-x-sm flex">
                    <DashboardPreviewCarousel
                        height={150}
                        width={150}
                        dashboards={extractDashboardPreviewItems(sessionData)}
                    />
                    <div className="gap-y-xs flex flex-col">
                        <div className="gap-y-4xs flex flex-col">
                            <strong className="text-body-xs text-neutral-subtle">Title</strong>
                            {sessionData.metadata.title}
                        </div>
                        <div className="gap-y-4xs flex flex-col">
                            <strong className="text-body-xs text-neutral-subtle">Last modified</strong>
                            <TimeAgo datetimeMs={sessionData.metadata.lastModifiedMs} />
                        </div>
                        <div className="gap-y-4xs flex flex-col">
                            <strong className="text-body-xs text-neutral-subtle">Last saved</strong>
                            <TimeAgo datetimeMs={activeSession.getMetadata().lastModifiedMs} />
                        </div>
                    </div>
                </div>
            </div>
        </AlertDialog>
    );
}
